import { Bell, FileCheck } from "lucide-react";
import { TableRow, TableCell } from "@/components/ui/table";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { RoleSelect } from "./RoleSelect";
import { UserVenuesDialog } from "./UserVenuesDialog";
import { DynamicIcon } from "@/components/categories/DynamicIcon";
import type { User, Category } from "@/types";

function formatDate(dateStr: string) {
  return new Intl.DateTimeFormat("pl-PL", {
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(new Date(dateStr));
}

function formatDateOnly(dateStr: string) {
  // Inputs are date-only strings like "2026-04-27". Anchor parsing AND formatting
  // to UTC so the calendar date stays exact regardless of viewer timezone.
  return new Intl.DateTimeFormat("pl-PL", {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  }).format(new Date(`${dateStr}T00:00:00Z`));
}

interface UserRowProps {
  user: User;
  categories: Category[];
}

export function UserRow({ user, categories }: UserRowProps) {
  const subscribedCategories = categories.filter((c) => user.notificationCategories.includes(c.id));

  return (
    <TableRow>
      <TableCell className="text-sm">{user.email}</TableCell>
      <TableCell>
        <RoleSelect userId={user.id} currentRole={user.role} />
      </TableCell>
      <TableCell>
        <div className="flex flex-wrap gap-1">
          {subscribedCategories.length === 0 ? (
            <span className="text-xs text-muted-foreground">—</span>
          ) : (
            subscribedCategories.map((cat) => {
              const colorHex = cat.colorHex.startsWith("#") ? cat.colorHex : `#${cat.colorHex}`;
              return (
                <Tooltip key={cat.id}>
                  <TooltipTrigger>
                    <div
                      className="w-7 h-7 rounded-md flex items-center justify-center shrink-0"
                      style={{ backgroundColor: `${colorHex}20` }}
                    >
                      <DynamicIcon
                        name={cat.iconName}
                        className="w-4 h-4"
                        style={{ color: colorHex }}
                      />
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="font-medium">{cat.name}</p>
                    <p className="text-xs text-muted-foreground">{cat.id}</p>
                  </TooltipContent>
                </Tooltip>
              );
            })
          )}
        </div>
      </TableCell>
      <TableCell>
        <div className="flex gap-1">
          <Tooltip>
            <TooltipTrigger>
              <div className="w-7 h-7 rounded-md flex items-center justify-center shrink-0 bg-emerald-50 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300">
                <FileCheck className="w-4 h-4" />
              </div>
            </TooltipTrigger>
            <TooltipContent>
              <p className="font-medium">Zaakceptowane wersje</p>
              <p className="text-xs text-muted-foreground">
                Regulamin: {formatDateOnly(user.acceptedTosVersion)}
              </p>
              <p className="text-xs text-muted-foreground">
                Prywatność: {formatDateOnly(user.acceptedPrivacyVersion)}
              </p>
            </TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger>
              <div className="w-7 h-7 rounded-md flex items-center justify-center shrink-0 bg-blue-50 text-blue-700 dark:bg-blue-950 dark:text-blue-300">
                <Bell className="w-4 h-4" />
              </div>
            </TooltipTrigger>
            <TooltipContent>
              <p className="font-medium">Powiadomiono o wersjach</p>
              <p className="text-xs text-muted-foreground">
                Regulamin: {formatDateOnly(user.notifiedTosVersion)}
              </p>
              <p className="text-xs text-muted-foreground">
                Prywatność: {formatDateOnly(user.notifiedPrivacyVersion)}
              </p>
            </TooltipContent>
          </Tooltip>
        </div>
      </TableCell>
      <TableCell className="text-sm text-muted-foreground">{formatDate(user.createdAt)}</TableCell>
      <TableCell>
        {user.role === "organizer" && <UserVenuesDialog userId={user.id} userEmail={user.email} />}
      </TableCell>
    </TableRow>
  );
}
